import { Injectable, inject } from '@angular/core';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { PageTitleService } from './page-title.service';

/** Numeric ids and guids, which must never appear in a page title. */
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * The single source of the document title (Bug 33081).
 *
 * Every screen needs its own `title`. Do not rely on the URL fallback to cover child routes: it
 * only fires when *nothing* on the activated branch has a title, and every top-level route is
 * titled. A child of a titled parent silently inherits the parent's name instead — which is how
 * several distinct screens ended up sharing one tab title. Humanization exists solely so a route
 * outside the titled tree cannot be left showing the *previous* page's title.
 *
 * Because titles come only from route config and the URL, no learner name can reach the tab.
 */
@Injectable({ providedIn: 'root' })
export class AchieveTitleStrategy extends TitleStrategy {
  private readonly pageTitleService = inject(PageTitleService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.pageTitleService.setTitle(this.buildTitle(snapshot) ?? this.humanizeUrl(snapshot.url));
  }

  private humanizeUrl(url: string): string {
    const segment = url
      .split(/[?#]/)[0]
      .split('/')
      .filter((part) => !!part && !ID_SEGMENT.test(part))
      .pop();

    if (!segment) {
      return '';
    }

    return decodeURIComponent(segment)
      .replace(/[-_]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }
}
